<template>
  <div class="tuition-details-fields">
    <div class="tuition-details-line">
      <span class="tuition-details-prefix text-dark-1">{{ seekingPrefix }}</span>
      <CalculatorSelectMenu
        id="tuition-calculator-degree-type"
        :model-value="props.modelValue.degreeType"
        :options="degreeOptions"
        :aria-describedby="degreeErrorId"
        accessible-label="Degree type"
        data-testid="degree-type"
        placeholder="select a degree type"
        @update:model-value="handleDegreeChange"
      />
      <p
        v-if="props.errors.degreeType"
        id="tuition-calculator-degree-type-error"
        class="text-danger"
      >
        {{ props.errors.degreeType }}
      </p>
    </div>

    <div class="tuition-details-line tuition-details-line--program">
      <span class="tuition-details-prefix text-dark-1">in</span>
      <CalculatorSelectMenu
        id="tuition-calculator-program"
        :model-value="props.modelValue.program ? String(props.modelValue.program.id) : null"
        :options="programOptions"
        :disabled="!props.modelValue.degreeType"
        :aria-describedby="programErrorId"
        accessible-label="Program"
        data-testid="program-select"
        placeholder="select a program"
        searchable
        @update:model-value="handleProgramChange"
      />
      <p v-if="props.errors.program" id="tuition-calculator-program-error" class="text-danger">
        {{ props.errors.program }}
      </p>
    </div>

    <div v-if="showEnrollmentStatus" class="tuition-details-line">
      <span class="tuition-details-prefix text-dark-1">as a</span>
      <CalculatorSelectMenu
        id="tuition-calculator-credit-option"
        :model-value="props.modelValue.creditOption"
        :options="creditOptions"
        :disabled="!props.modelValue.program"
        :aria-describedby="creditsErrorId"
        accessible-label="Enrollment status"
        data-testid="credit-option"
        placeholder="select enrollment status"
        @update:model-value="handleCreditOptionChange"
      />
      <p
        v-if="props.errors.creditsPerSemester"
        id="tuition-calculator-credits-error"
        class="text-danger"
      >
        {{ props.errors.creditsPerSemester }}
      </p>
    </div>

    <ResidencyOptions
      v-if="showResidency"
      :model-value="props.modelValue.residencyStatus"
      :title="props.residencyStatusTitle"
      :description="props.residencyStatusDescription"
      :error="props.errors.residencyStatus"
      :error-id="residencyErrorId ?? 'tuition-calculator-residency-error'"
      @update:model-value="handleResidencyChange"
      @select="emit('selection', 'residencyStatus', $event)"
      @faq-link="emit('faq-link', $event)"
    />

    <CreditInputModal
      :visible="manualModalVisible"
      :model-value="props.modelValue.creditsPerSemester"
      :degree-type="props.modelValue.degreeType"
      :warning-message="props.creditsWarningMessage"
      @confirm="handleManualConfirm"
      @close="handleManualClose"
      @close-cross="emit('credit-modal-close-cross')"
      @cancel="emit('credit-modal-cancel')"
      @credits-change="emit('credits-change', $event)"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";

import CalculatorSelectMenu, {
  type CalculatorSelectOption
} from "~/components/what-it-costs-page/cost-calculator-v2/CalculatorSelectMenu.vue";
import CreditInputModal from "~/components/what-it-costs-page/cost-calculator-v2/CreditInputModal.vue";
import ResidencyOptions from "~/components/what-it-costs-page/cost-calculator-v2/ResidencyOptions.vue";
import type { DegreeSeekingProgram } from "~/server/resolvers/resolvers-types";
import { TUITION_CREDIT_OPTION_LABELS, TUITION_DEGREE_TYPE_LABELS } from "~/src/services/constants";
import {
  filterPrograms,
  getFullTimeCredits,
  residencyAffectsCost,
  validateDetails
} from "~/src/services/TuitionCalculatorFormService";
import type {
  FaqLinkTarget,
  TuitionCalculatorCreditOption,
  TuitionCalculatorDegreeType,
  TuitionCalculatorResidency,
  TuitionCalculatorSelections,
  TuitionCalculatorValidationErrors
} from "~/types/tuition-calculator";

const props = defineProps<{
  modelValue: TuitionCalculatorSelections;
  programs: DegreeSeekingProgram[];
  errors: TuitionCalculatorValidationErrors;
  creditsWarningMessage: string;
  residencyStatusTitle?: string | null;
  residencyStatusDescription?: string | null;
}>();

const emit = defineEmits<{
  (event: "update:modelValue", value: TuitionCalculatorSelections): void;
  (event: "selection", field: string, text: string): void;
  (event: "credit-modal-open" | "credit-modal-close-cross" | "credit-modal-cancel"): void;
  (event: "credit-modal-confirm" | "credits-change", value: number): void;
  (event: "faq-link", target: FaqLinkTarget): void;
}>();

const degreeOptions: CalculatorSelectOption[] = [
  {
    value: "Undergraduate",
    text: TUITION_DEGREE_TYPE_LABELS.Undergraduate,
    selectedText: TUITION_DEGREE_TYPE_LABELS.Undergraduate
  },
  {
    value: "Graduate",
    text: TUITION_DEGREE_TYPE_LABELS.Graduate,
    selectedText: TUITION_DEGREE_TYPE_LABELS.Graduate
  },
  {
    value: "Certificates",
    text: TUITION_DEGREE_TYPE_LABELS.Certificates,
    selectedText: TUITION_DEGREE_TYPE_LABELS.Certificates
  }
];

const defaultCreditOptions: CalculatorSelectOption[] = [
  {
    value: "part-time",
    text: TUITION_CREDIT_OPTION_LABELS["part-time"],
    selectedText: TUITION_CREDIT_OPTION_LABELS["part-time"].toLowerCase(),
    description: "Typically 6 credits per semester"
  },
  {
    value: "full-time",
    text: TUITION_CREDIT_OPTION_LABELS["full-time"],
    selectedText: TUITION_CREDIT_OPTION_LABELS["full-time"].toLowerCase(),
    description: "12+ credits per semester"
  },
  {
    value: "manual",
    text: TUITION_CREDIT_OPTION_LABELS.manual,
    description: "Choose your own pace"
  }
];

const creditOptions = computed<CalculatorSelectOption[]>(() =>
  defaultCreditOptions.map((option) => {
    if (option.value === "full-time" && props.modelValue.degreeType) {
      const fullTimeCredits = getFullTimeCredits(props.modelValue.degreeType);
      return {
        ...option,
        description: `${fullTimeCredits}+ credits per semester`
      };
    }

    if (
      option.value !== "manual" ||
      props.modelValue.creditOption !== "manual" ||
      props.modelValue.creditsPerSemester <= 0
    )
      return option;

    const creditUnit = props.modelValue.creditsPerSemester === 1 ? "credit" : "credits";
    return {
      ...option,
      selectedText: `student taking ${props.modelValue.creditsPerSemester} ${creditUnit}`
    };
  })
);

const seekingPrefix = computed(() =>
  props.modelValue.degreeType === "Undergraduate" ? "I’m seeking an" : "I’m seeking a"
);

const manualModalVisible = ref(false);
type ManualSelectionSnapshot = Pick<
  TuitionCalculatorSelections,
  "creditOption" | "creditsPerSemester"
>;
const manualSelectionBeforeOpen = ref<ManualSelectionSnapshot | null>(null);

const programOptions = computed<CalculatorSelectOption[]>(() => {
  if (!props.modelValue.degreeType) return [];
  return filterPrograms(props.programs, props.modelValue.degreeType).map((program) => ({
    value: String(program.id),
    text: program.title ?? "",
    selectedText: (program.title ?? "").toLowerCase()
  }));
});

const showEnrollmentStatus = computed(() => props.modelValue.degreeType !== "Certificates");

const showResidency = computed(() =>
  Boolean(
    props.modelValue.degreeType &&
    props.modelValue.degreeType !== "Certificates" &&
    residencyAffectsCost(props.modelValue) &&
    !validateDetails(props.modelValue).creditsPerSemester
  )
);

const degreeErrorId = computed(() =>
  props.errors.degreeType ? "tuition-calculator-degree-type-error" : undefined
);
const programErrorId = computed(() =>
  props.errors.program ? "tuition-calculator-program-error" : undefined
);
const creditsErrorId = computed(() =>
  props.errors.creditsPerSemester ? "tuition-calculator-credits-error" : undefined
);
const residencyErrorId = computed(() =>
  props.errors.residencyStatus ? "tuition-calculator-residency-error" : undefined
);

const updateModel = (updates: Partial<TuitionCalculatorSelections>) => {
  emit("update:modelValue", { ...props.modelValue, ...updates });
};

const handleDegreeChange = (value: string | null) => {
  const degreeType = value ? (value as TuitionCalculatorDegreeType) : null;
  manualSelectionBeforeOpen.value = null;
  updateModel({
    degreeType,
    program: null,
    creditOption: null,
    creditsPerSemester: 0,
    residencyStatus: null
  });
  emit(
    "selection",
    "degreeType",
    degreeOptions.find((option) => option.value === degreeType)?.text ?? ""
  );
};

const handleProgramChange = (value: string | null) => {
  const selectedProgram = value
    ? (props.programs.find((program) => String(program.id) === value) ?? null)
    : null;
  manualSelectionBeforeOpen.value = null;
  updateModel({
    program: selectedProgram,
    residencyStatus:
      !selectedProgram || selectedProgram.costOverride ? null : props.modelValue.residencyStatus
  });
  emit("selection", "program", selectedProgram?.title ?? "");
};

const handleCreditOptionChange = (value: string | null) => {
  const creditOption = value ? (value as TuitionCalculatorCreditOption) : null;

  if (!creditOption) {
    manualSelectionBeforeOpen.value = null;
    updateModel({
      creditOption: null,
      creditsPerSemester: 0,
      residencyStatus: null
    });
    emit("selection", "creditOption", "");
    return;
  }

  if (creditOption === "manual") {
    manualSelectionBeforeOpen.value = {
      creditOption: props.modelValue.creditOption,
      creditsPerSemester: props.modelValue.creditsPerSemester
    };
    updateModel({ creditOption, creditsPerSemester: 0 });
    emit("credit-modal-open");
    manualModalVisible.value = true;
    return;
  }

  if (!props.modelValue.degreeType) return;
  const creditsPerSemester =
    creditOption === "part-time" ? 6 : getFullTimeCredits(props.modelValue.degreeType);
  updateModel({ creditOption, creditsPerSemester });
  emit(
    "selection",
    "creditOption",
    defaultCreditOptions.find((option) => option.value === creditOption)?.text ?? ""
  );
};

const handleManualConfirm = (creditsPerSemester: number) => {
  manualSelectionBeforeOpen.value = null;
  updateModel({
    creditOption: "manual",
    creditsPerSemester
  });
  emit("credit-modal-confirm", creditsPerSemester);
  void closeManualModal(false);
};

const closeManualModal = async (restoreSelection: boolean) => {
  if (restoreSelection && manualSelectionBeforeOpen.value) {
    updateModel(manualSelectionBeforeOpen.value);
  }
  manualSelectionBeforeOpen.value = null;
  manualModalVisible.value = false;
  await nextTick();
  document.getElementById("tuition-calculator-credit-option-trigger")?.focus();
};

const handleManualClose = () => {
  void closeManualModal(true);
};

const handleResidencyChange = (value: TuitionCalculatorResidency) => {
  updateModel({ residencyStatus: value });
};
</script>

<style scoped lang="scss">
.tuition-details-fields {
  color: var(--rds-dark-3, #747474);
}

.tuition-details-line {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.tuition-details-prefix {
  flex: 0 0 auto;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  line-height: 1.25;
}

.tuition-details-line :deep(.calculator-select__trigger) {
  max-width: 100%;
  min-width: 0;
  white-space: nowrap;
}

.tuition-details-line :deep(.calculator-select__value) {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@media (min-width: 992px) {
  .tuition-details-line--program {
    flex-wrap: nowrap;
  }

  .tuition-details-line--program :deep(.calculator-select) {
    flex: 1 1 auto;
    min-width: 0;
    max-width: 34rem;
  }

  .tuition-details-line--program :deep(#tuition-calculator-program-trigger) {
    width: max-content;
    max-width: min(100%, 34rem);
  }
}

@media (max-width: 991.98px) {
  .tuition-details-line {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 0.4rem;
    margin-bottom: 1.75rem;
  }

  .tuition-details-prefix {
    width: 100%;
  }

  .tuition-details-line :deep(.calculator-select) {
    display: flex;
    width: 100%;
  }

  .tuition-details-line :deep(.calculator-select__trigger) {
    width: 100%;
    min-width: 0;
    justify-content: space-between;
    padding-right: 0;
  }
}

@media (max-width: 575.98px) {
  .tuition-residency__option {
    width: 100%;
  }
}
</style>
